import { useEffect, useState } from 'react';

const TOAST_TYPES = ['success', 'error', 'warning', 'info'];

/**
 * Single toast item with theme-based styling, auto-dismiss, and optional manual close.
 */
export function ToastItem({ id, type, message, duration, onDismiss }) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration <= 0) return;
    const t = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onDismiss, 300);
    }, duration);
    return () => clearTimeout(t);
  }, [duration, onDismiss]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onDismiss, 300);
  };

  const typeKey = TOAST_TYPES.includes(type) ? type : 'info';
  const bgVar = `--toast-${typeKey}-bg`;
  const borderVar = `--toast-${typeKey}-border`;
  const accentVar = `--toast-${typeKey}-accent`;

  return (
    <div
      role="alert"
      aria-live="polite"
      data-toast-type={typeKey}
      style={{
        background: `var(${bgVar})`,
        border: `1px solid var(${borderVar})`,
        borderRadius: 'var(--radius-md)',
        padding: '0.875rem 1rem',
        minWidth: '280px',
        maxWidth: '400px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        boxShadow: 'var(--shadow-card)',
        fontFamily: 'var(--font-body)',
        color: 'var(--text-primary)',
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'translateX(100%)' : 'translateX(0)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        animation: 'toastSlideIn 0.3s ease',
      }}
    >
      <div
        style={{
          width: '4px',
          alignSelf: 'stretch',
          borderRadius: 2,
          background: `var(${accentVar})`,
          flexShrink: 0,
        }}
      />
      <p style={{ flex: 1, fontSize: '0.9rem', lineHeight: 1.5, margin: 0 }}>
        {message}
      </p>
      <button
        type="button"
        onClick={handleClose}
        aria-label="Close"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          padding: '0.2rem',
          fontSize: '1rem',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

/**
 * Container for stacked toasts, top-right positioned.
 */
export function ToastContainer({ toasts, onDismiss }) {
  return (
    <div
      aria-label="Notifications"
      style={{
        position: 'fixed',
        top: '1rem',
        right: '1rem',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        maxWidth: 'min(400px, calc(100vw - 2rem))',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          pointerEvents: 'auto',
        }}
      >
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            id={t.id}
            type={t.type}
            message={t.message}
            duration={t.duration}
            onDismiss={() => onDismiss(t.id)}
          />
        ))}
      </div>
    </div>
  );
}
